import React, { useEffect, useState } from "react";
import { api, formatDateTime, errMsg, formatIDR } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { CompanySimulation } from "@/components/CompanySimulation";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Shield, Zap, AlertTriangle, KeyRound, Plus, Trash2, RefreshCw, MessageSquare, Users as UsersIcon, FileSpreadsheet, Upload, Download } from "lucide-react";
import { useTableFilter } from "@/lib/useTableFilter";
import { SortHeader, TableToolbar } from "@/components/TableTools";

const ROLES = [
  { v: "owner", l: "Owner" },
  { v: "manager_ops", l: "Manager Operasional" },
  { v: "finance", l: "Finance" },
  { v: "kasir", l: "Kasir" },
  { v: "mitra", l: "Mitra (Toko)" },
  { v: "investor", l: "Investor" },
  { v: "staff", l: "Staff" },
];

export default function SuperAdmin() {
  const [users, setUsers] = useState([]);
  const [stores, setStores] = useState([]);
  const [logs, setLogs] = useState([]);
  const [complaints, setComplaints] = useState([]);
  const [simOpen, setSimOpen] = useState(false);
  const [clearOpen, setClearOpen] = useState(false);
  const [clearPw, setClearPw] = useState("");
  const [openUser, setOpenUser] = useState(false);
  const [editing, setEditing] = useState(null);
  const [uForm, setUForm] = useState({ name: "", email: "", username: "", password: "", role: "staff", store_id: "", division: "", invested_amount: 0 });
  const [pwDialog, setPwDialog] = useState(null);
  const [newPw, setNewPw] = useState("");
  const [launchOpen, setLaunchOpen] = useState(false);
  const [launchPw, setLaunchPw] = useState("");
  const [launchBusy, setLaunchBusy] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState(new Set());

  const loadAll = () => Promise.all([
    api.get("/users").then((r) => setUsers(r.data)),
    api.get("/stores").then((r) => setStores(r.data)),
    api.get("/admin/activity-logs?days=90").then((r) => setLogs(r.data)),
    api.get("/admin/complaints").then((r) => setComplaints(r.data)),
  ]);
  useEffect(() => { loadAll(); }, []);

  const doClearAll = async () => {
    if (!clearPw) return toast.error("Masukkan password owner Anda");
    try {
      const { data } = await api.post("/admin/clear-all-data", { password: clearPw });
      toast.success(data.message || "Semua data operasional telah dihapus.");
      setClearOpen(false); setClearPw("");
      loadAll();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const doLaunchReset = async () => {
    if (!launchPw) return toast.error("Masukkan password owner Anda");
    setLaunchBusy(true);
    try {
      const { data } = await api.post("/admin/reset-portal-data", { password: launchPw });
      toast.success(data.message);
      setLaunchOpen(false); setLaunchPw("");
      loadAll();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setLaunchBusy(false); }
  };

  const openNewUser = () => {
    setEditing(null);
    setUForm({ name: "", email: "", username: "", password: "", role: "staff", store_id: "", division: "", invested_amount: 0 });
    setOpenUser(true);
  };
  const openEditUser = (u) => {
    setEditing(u);
    setUForm({
      name: u.name || "", email: u.email || "", username: u.username || "", password: "",
      role: u.role, store_id: u.store_id || "", division: u.division || "",
      invested_amount: u.invested_amount || 0,
    });
    setOpenUser(true);
  };
  const saveUser = async () => {
    try {
      if (editing) {
        const upd = { ...uForm };
        delete upd.password;
        await api.put(`/users/${editing.id}`, upd);
        toast.success("User diperbarui");
      } else {
        if (!uForm.password) return toast.error("Password wajib untuk user baru");
        await api.post("/users", uForm);
        toast.success("User dibuat");
      }
      setOpenUser(false); loadAll();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const delUser = async (id) => {
    if (!window.confirm("Hapus user ini?")) return;
    try { await api.delete(`/users/${id}`); toast.success("Dihapus"); loadAll(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const bulkDeleteUsers = async () => {
    if (selectedUsers.size === 0) return;
    if (!window.confirm(`Hapus ${selectedUsers.size} user sekaligus? Aksi ini tidak bisa dibatalkan.`)) return;
    try {
      const { data } = await api.post("/users/bulk-delete", { ids: Array.from(selectedUsers) });
      toast.success(`${data.deleted} user terhapus`);
      setSelectedUsers(new Set());
      loadAll();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const toggleUserSelect = (id) => {
    const next = new Set(selectedUsers);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelectedUsers(next);
  };
  const toggleAllUsers = (checked, rows) => {
    if (checked) setSelectedUsers(new Set(rows.map((r) => r.id)));
    else setSelectedUsers(new Set());
  };
  const doResetPw = async () => {
    if (!newPw || newPw.length < 4) return toast.error("Password min 4 karakter");
    try {
      await api.post(`/users/${pwDialog.id}/reset-password`, { new_password: newPw });
      toast.success(`Password ${pwDialog.username || pwDialog.email} berhasil di-reset`);
      setPwDialog(null); setNewPw("");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const respondComplaint = async (c) => {
    const resp = window.prompt(`Respon untuk keluhan "${c.subject}" dari ${c.store_name}:`, c.response || "");
    if (resp === null) return;
    try {
      await api.put(`/admin/complaints/${c.id}`, { response: resp, status: "responded" });
      toast.success("Respon dikirim");
      loadAll();
    } catch (e) { toast.error(errMsg(e)); }
  };

  // Export users to Excel
  const exportUsers = async () => {
    try {
      const XLSX = await import("xlsx");
      const rows = users.map((u, i) => ({
        No: i + 1, "User ID": u.user_id || "-", Nama: u.name, Username: u.username, Email: u.email,
        Role: u.role, "Toko/Divisi": u.store_name || u.division || "", "Dibuat": u.created_at || "",
      }));
      const ws = XLSX.utils.json_to_sheet(rows);
      ws["!cols"] = [{ wch: 4 }, { wch: 10 }, { wch: 30 }, { wch: 25 }, { wch: 30 }, { wch: 15 }, { wch: 30 }, { wch: 20 }];
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Users");
      XLSX.writeFile(wb, `hotto_users_${new Date().toISOString().slice(0,10)}.xlsx`);
      toast.success(`${rows.length} user diunduh`);
    } catch (e) { toast.error("Gagal export: " + e.message); }
  };

  const exportLogs = async () => {
    try {
      const XLSX = await import("xlsx");
      const rows = logs.map((l) => ({
        Waktu: l.created_at, User: l.username || "-", Role: l.role || "-", Aksi: l.action,
        Method: l.method, Endpoint: l.path, Status: l.status, IP: l.ip || "",
      }));
      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Activity Logs");
      XLSX.writeFile(wb, `hotto_activity_logs_${new Date().toISOString().slice(0,10)}.xlsx`);
      toast.success(`${rows.length} log diunduh`);
    } catch (e) { toast.error("Gagal export: " + e.message); }
  };

  const downloadTemplate = async () => {
    try {
      const XLSX = await import("xlsx");
      const sample = [
        { name: "Contoh Nama", email: "contoh@email.com", username: "contoh", password: "hotto123", role: "staff", division: "operasional", store_id: "" },
      ];
      const ws = XLSX.utils.json_to_sheet(sample);
      ws["!cols"] = [{ wch: 20 }, { wch: 25 }, { wch: 20 }, { wch: 15 }, { wch: 12 }, { wch: 15 }, { wch: 35 }];
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Users Template");
      XLSX.writeFile(wb, "hotto_users_template.xlsx");
    } catch (e) { toast.error(e.message); }
  };

  const importUsers = async (file) => {
    if (!file) return;
    try {
      const XLSX = await import("xlsx");
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: "array" });
      const rows = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]]);
      if (!rows.length) return toast.error("File kosong");
      const { data } = await api.post("/users/bulk-import", { rows });
      toast.success(`Import: ${data.created} baru, ${data.skipped} skip, ${data.errors.length} error`);
      if (data.errors.length) console.warn("Import errors:", data.errors);
      loadAll();
    } catch (e) { toast.error("Gagal import: " + e.message); }
  };

  const userTF = useTableFilter(users, {
    searchKeys: ["name", "email", "username", "role", "store_name", "user_id"],
    defaultSort: { key: "role", dir: "asc" },
  });
  const logTF = useTableFilter(logs, {
    searchKeys: ["username", "name", "path", "action", "method"],
    defaultSort: { key: "created_at", dir: "desc" },
  });
  const complaintTF = useTableFilter(complaints, {
    searchKeys: ["store_name", "subject", "category", "status"],
    defaultSort: { key: "created_at", dir: "desc" },
  });

  return (
    <>
      <PageHeader title="Super Admin" subtitle="Panel khusus owner: kontrol total sistem" testId="page-super-admin"
        actions={<div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={() => setSimOpen(true)}
            className="border-emerald-400 text-emerald-700 hover:bg-emerald-50" data-testid="btn-run-sim">
            <Zap className="w-4 h-4 mr-2"/> Simulasi
          </Button>
          <Button variant="outline" onClick={() => setLaunchOpen(true)}
            className="border-blue-500 text-blue-700 hover:bg-blue-50" data-testid="btn-launch-reset">
            <RefreshCw className="w-4 h-4 mr-2"/> Persiapan Launch
          </Button>
          <Button variant="outline" onClick={() => setClearOpen(true)}
            className="border-red-400 text-red-700 hover:bg-red-50" data-testid="btn-clear-all">
            <AlertTriangle className="w-4 h-4 mr-2"/> Hapus Semua Data
          </Button>
        </div>}
      />

      <CompanySimulation open={simOpen} onOpenChange={setSimOpen}/>

      <Tabs defaultValue="users">
        <TabsList className="mb-4">
          <TabsTrigger value="users" data-testid="tab-users"><UsersIcon className="w-4 h-4 mr-2"/>Manajemen User ({users.length})</TabsTrigger>
          <TabsTrigger value="logs" data-testid="tab-logs"><Shield className="w-4 h-4 mr-2"/>Log Aktivitas ({logs.length})</TabsTrigger>
          <TabsTrigger value="complaints" data-testid="tab-complaints"><MessageSquare className="w-4 h-4 mr-2"/>Keluhan Mitra ({complaints.length})</TabsTrigger>
        </TabsList>

        {/* ============ USERS ============ */}
        <TabsContent value="users">
          <div className="mb-3 flex items-center justify-between flex-wrap gap-3">
            <div className="text-xs text-slate-600 bg-amber-50 border border-amber-200 rounded-md p-2 max-w-2xl">
              <b>Catatan keamanan:</b> Password disimpan ter-hash (bcrypt) sehingga <b>tidak bisa dilihat</b>, bahkan oleh owner. Untuk mengganti password user, gunakan tombol <b>Reset</b>.
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button variant="outline" size="sm" onClick={downloadTemplate} data-testid="btn-user-template" className="border-slate-300">
                <Download className="w-3 h-3 mr-1"/> Template
              </Button>
              <label className="inline-flex cursor-pointer">
                <input type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={(e) => importUsers(e.target.files?.[0])} data-testid="input-import-users"/>
                <span className="inline-flex items-center h-8 px-3 rounded-md border border-blue-400 text-blue-700 text-xs font-medium hover:bg-blue-50">
                  <Upload className="w-3 h-3 mr-1"/> Import Excel
                </span>
              </label>
              <Button variant="outline" size="sm" onClick={exportUsers} data-testid="btn-export-users" className="border-emerald-400 text-emerald-700">
                <FileSpreadsheet className="w-3 h-3 mr-1"/> Export
              </Button>
              <Button onClick={openNewUser} data-testid="btn-add-user" size="sm" className="bg-[var(--hotto-primary)] hover:bg-[var(--hotto-primary-hover)]">
                <Plus className="w-4 h-4 mr-1"/> User Baru
              </Button>
            </div>
          </div>
          <Card className="border border-[var(--hotto-border)] rounded-md overflow-hidden">
            <div className="p-3 border-b flex items-center justify-between gap-2 flex-wrap">
              <TableToolbar tf={userTF} placeholder="Cari nama / email / username / user ID..."/>
              {selectedUsers.size > 0 && (
                <div className="flex items-center gap-2 px-3 py-1.5 bg-red-50 border border-red-200 rounded-md" data-testid="user-bulk-toolbar">
                  <span className="text-xs text-red-800 font-medium">{selectedUsers.size} terpilih</span>
                  <Button size="sm" variant="destructive" onClick={bulkDeleteUsers} data-testid="btn-bulk-delete-users" className="h-7 text-xs">
                    <Trash2 className="w-3 h-3 mr-1"/> Hapus Terpilih
                  </Button>
                  <Button size="sm" variant="ghost" onClick={()=>setSelectedUsers(new Set())} className="h-7 text-xs">Batal</Button>
                </div>
              )}
            </div>
            <Table>
              <TableHeader><TableRow className="bg-slate-50">
                <TableHead className="w-8">
                  <Checkbox
                    checked={userTF.rows.length > 0 && userTF.rows.every((r) => selectedUsers.has(r.id))}
                    onCheckedChange={(c)=>toggleAllUsers(c, userTF.rows)}
                    data-testid="check-all-users"
                  />
                </TableHead>
                <TableHead className="w-10">#</TableHead>
                <SortHeader tf={userTF} col="user_id">User ID</SortHeader>
                <SortHeader tf={userTF} col="role">Role</SortHeader>
                <SortHeader tf={userTF} col="name">Nama</SortHeader>
                <SortHeader tf={userTF} col="username">Username</SortHeader>
                <SortHeader tf={userTF} col="email">Email</SortHeader>
                <SortHeader tf={userTF} col="store_name">Toko / Divisi</SortHeader>
                <TableHead className="w-52 text-right">Aksi</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {userTF.rows.map((u, idx) => (
                  <TableRow key={u.id} className={`table-row-hover ${selectedUsers.has(u.id)?"bg-red-50/40":""}`}>
                    <TableCell><Checkbox checked={selectedUsers.has(u.id)} onCheckedChange={()=>toggleUserSelect(u.id)} data-testid={`check-user-${u.id}`}/></TableCell>
                    <TableCell className="font-mono-num text-slate-400 text-sm">{idx + 1}</TableCell>
                    <TableCell>
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-emerald-50 border border-emerald-200 font-mono-num text-xs font-bold text-emerald-800" data-testid={`uid-${u.id}`}>
                        {u.user_id || "—"}
                      </span>
                    </TableCell>
                    <TableCell><Badge variant="outline" className="uppercase text-[10px]">{u.role}</Badge></TableCell>
                    <TableCell className="font-medium">{u.name}</TableCell>
                    <TableCell className="font-mono-num text-xs">{u.username}</TableCell>
                    <TableCell className="text-slate-600 text-sm">{u.email}</TableCell>
                    <TableCell className="text-xs text-slate-500">{u.store_name || u.division || "-"}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button size="sm" variant="outline" onClick={() => { setPwDialog(u); setNewPw(""); }} data-testid={`btn-reset-${u.id}`}>
                          <KeyRound className="w-3 h-3 mr-1"/> Reset PW
                        </Button>
                        <Button size="icon" variant="ghost" onClick={() => openEditUser(u)}>✎</Button>
                        <Button size="icon" variant="ghost" onClick={() => delUser(u.id)}><Trash2 className="w-4 h-4 text-red-500"/></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        {/* ============ ACTIVITY LOGS ============ */}
        <TabsContent value="logs">
          <Card className="border border-[var(--hotto-border)] rounded-md overflow-hidden">
            <div className="p-3 border-b flex items-center gap-3 flex-wrap">
              <TableToolbar tf={logTF} placeholder="Cari user / endpoint..."/>
              <Button variant="outline" size="sm" onClick={loadAll}><RefreshCw className="w-3 h-3 mr-1"/> Refresh</Button>
              <Button variant="outline" size="sm" onClick={exportLogs} data-testid="btn-export-logs" className="border-emerald-400 text-emerald-700">
                <FileSpreadsheet className="w-3 h-3 mr-1"/> Export Excel
              </Button>
            </div>
            <div className="max-h-[600px] overflow-y-auto">
              <Table>
                <TableHeader className="sticky top-0 bg-white z-10">
                  <TableRow className="bg-slate-50">
                    <SortHeader tf={logTF} col="created_at">Waktu</SortHeader>
                    <SortHeader tf={logTF} col="username">User</SortHeader>
                    <SortHeader tf={logTF} col="role">Role</SortHeader>
                    <SortHeader tf={logTF} col="action">Aksi</SortHeader>
                    <SortHeader tf={logTF} col="method">Method</SortHeader>
                    <SortHeader tf={logTF} col="path">Endpoint</SortHeader>
                    <SortHeader tf={logTF} col="status">Status</SortHeader>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {logTF.rows.length === 0 && <TableRow><TableCell colSpan={7} className="text-center py-8 text-slate-500">Tidak ada log.</TableCell></TableRow>}
                  {logTF.rows.slice(0, 500).map((l) => (
                    <TableRow key={l.id} className="table-row-hover">
                      <TableCell className="font-mono-num text-xs">{formatDateTime(l.created_at)}</TableCell>
                      <TableCell className="text-xs font-medium">{l.username || "-"}</TableCell>
                      <TableCell className="text-xs">{l.role || "-"}</TableCell>
                      <TableCell><Badge variant="outline" className="text-[10px]">{l.action}</Badge></TableCell>
                      <TableCell className="text-xs font-mono-num">{l.method}</TableCell>
                      <TableCell className="text-xs text-slate-600 font-mono-num max-w-md truncate">{l.path}</TableCell>
                      <TableCell><Badge className={l.status < 300 ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-100" : "bg-red-100 text-red-700 hover:bg-red-100"}>{l.status}</Badge></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </Card>
        </TabsContent>

        {/* ============ COMPLAINTS ============ */}
        <TabsContent value="complaints">
          <Card className="border border-[var(--hotto-border)] rounded-md overflow-hidden">
            <div className="p-3 border-b"><TableToolbar tf={complaintTF} placeholder="Cari toko / subjek..."/></div>
            <Table>
              <TableHeader><TableRow className="bg-slate-50">
                <SortHeader tf={complaintTF} col="created_at">Waktu</SortHeader>
                <SortHeader tf={complaintTF} col="store_name">Toko</SortHeader>
                <SortHeader tf={complaintTF} col="category">Kategori</SortHeader>
                <SortHeader tf={complaintTF} col="subject">Subjek</SortHeader>
                <SortHeader tf={complaintTF} col="priority">Prio</SortHeader>
                <SortHeader tf={complaintTF} col="status">Status</SortHeader>
                <TableHead className="text-right w-32">Aksi</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {complaintTF.rows.length === 0 && <TableRow><TableCell colSpan={7} className="text-center py-8 text-slate-500">Belum ada keluhan.</TableCell></TableRow>}
                {complaintTF.rows.map((c) => (
                  <TableRow key={c.id} className="table-row-hover">
                    <TableCell className="text-xs font-mono-num">{formatDateTime(c.created_at)}</TableCell>
                    <TableCell className="font-medium">{c.store_name}</TableCell>
                    <TableCell><Badge variant="outline" className="uppercase text-[10px]">{c.category}</Badge></TableCell>
                    <TableCell>
                      <div className="font-medium text-sm">{c.subject}</div>
                      <div className="text-xs text-slate-500 max-w-md truncate">{c.message}</div>
                      {c.response && <div className="text-xs text-emerald-700 mt-1"><b>Respon:</b> {c.response}</div>}
                    </TableCell>
                    <TableCell><Badge className={c.priority === "high" ? "bg-red-100 text-red-700 hover:bg-red-100" : "bg-slate-100 text-slate-700 hover:bg-slate-100"}>{c.priority}</Badge></TableCell>
                    <TableCell><Badge className={c.status === "responded" ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-100" : "bg-amber-100 text-amber-700 hover:bg-amber-100"}>{c.status}</Badge></TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="outline" onClick={() => respondComplaint(c)}>Respon</Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>
      </Tabs>

      {/* ============ USER FORM DIALOG ============ */}
      <Dialog open={openUser} onOpenChange={setOpenUser}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editing ? "Edit User" : "User Baru"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Nama</Label><Input value={uForm.name} onChange={(e) => setUForm({ ...uForm, name: e.target.value })} data-testid="input-user-name"/></div>
              <div><Label>Role</Label>
                <Select value={uForm.role} onValueChange={(v) => setUForm({ ...uForm, role: v })}>
                  <SelectTrigger data-testid="select-role"><SelectValue/></SelectTrigger>
                  <SelectContent>{ROLES.map((r) => <SelectItem key={r.v} value={r.v}>{r.l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Email</Label><Input type="email" value={uForm.email} onChange={(e) => setUForm({ ...uForm, email: e.target.value })} data-testid="input-user-email"/></div>
              <div><Label>Username</Label><Input value={uForm.username} onChange={(e) => setUForm({ ...uForm, username: e.target.value })} placeholder="auto jika kosong" data-testid="input-user-username"/></div>
            </div>
            {!editing && (
              <div><Label>Password Awal</Label><Input type="password" value={uForm.password} onChange={(e) => setUForm({ ...uForm, password: e.target.value })} data-testid="input-user-password"/></div>
            )}
            {uForm.role === "mitra" && (
              <div><Label>Toko Binaan</Label>
                <Select value={uForm.store_id} onValueChange={(v) => setUForm({ ...uForm, store_id: v })}>
                  <SelectTrigger><SelectValue placeholder="Pilih toko"/></SelectTrigger>
                  <SelectContent>{stores.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            {uForm.role === "staff" && (
              <div><Label>Divisi</Label>
                <Select value={uForm.division} onValueChange={(v) => setUForm({ ...uForm, division: v })}>
                  <SelectTrigger><SelectValue placeholder="Pilih divisi"/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="operasional">Operasional</SelectItem>
                    <SelectItem value="finance">Finance</SelectItem>
                    <SelectItem value="kasir">Kasir</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            {uForm.role === "investor" && (
              <div><Label>Nominal Investasi (Rp)</Label><Input type="number" value={uForm.invested_amount} onChange={(e) => setUForm({ ...uForm, invested_amount: e.target.value })}/></div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenUser(false)}>Batal</Button>
            <Button onClick={saveUser} data-testid="btn-save-user" className="bg-[var(--hotto-primary)] hover:bg-[var(--hotto-primary-hover)]">Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ============ RESET PW ============ */}
      <Dialog open={!!pwDialog} onOpenChange={(v) => !v && setPwDialog(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Reset Password: {pwDialog?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="text-xs text-slate-500">Username: <b>{pwDialog?.username}</b> · Email: <b>{pwDialog?.email}</b></div>
            <div><Label>Password Baru</Label><Input type="text" value={newPw} onChange={(e) => setNewPw(e.target.value)} placeholder="min 4 karakter" data-testid="input-new-password"/></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPwDialog(null)}>Batal</Button>
            <Button onClick={doResetPw} data-testid="btn-do-reset" className="bg-[var(--hotto-primary)] hover:bg-[var(--hotto-primary-hover)]">Reset</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ============ CLEAR ALL ============ */}
      <Dialog open={clearOpen} onOpenChange={(v) => { if (!v) { setClearOpen(false); setClearPw(""); } }}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="text-red-700 flex items-center gap-2"><AlertTriangle className="w-5 h-5"/> Hapus SEMUA Data Operasional</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-800">
              <p className="font-semibold mb-2">⚠️ Tindakan ini PERMANEN dan tidak bisa dibatalkan!</p>
              <p className="text-xs mb-2"><b>Yang akan DIHAPUS:</b></p>
              <ul className="text-xs list-disc pl-5 space-y-0.5">
                <li>Pembelian, Penyaluran, Penjualan Langsung, Laporan Retail</li>
                <li>Pengeluaran &amp; Arus Kas</li>
                <li>Modal Awal, Penyesuaian Modal, Hutang</li>
                <li>Stok Titipan Toko &amp; Harga per Toko</li>
                <li>Kunci Laba Bulanan</li>
                <li>Riwayat Opname, Stock Adjustment, Audit Trail</li>
                <li>Keluhan, Pengumuman, Log Aktivitas</li>
              </ul>
              <p className="text-xs mt-2"><b className="text-emerald-700">TETAP AMAN:</b> Produk (stok direset 0), Distributor, Toko, Konsumen, User.</p>
            </div>
            <div><Label>Konfirmasi Password Owner</Label>
              <Input type="password" value={clearPw} onChange={(e) => setClearPw(e.target.value)} data-testid="input-clear-password"/>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setClearOpen(false); setClearPw(""); }}>Batal</Button>
            <Button onClick={doClearAll} disabled={!clearPw} className="bg-red-600 hover:bg-red-700 text-white" data-testid="btn-confirm-clear">
              Hapus Semua Data
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ============ LAUNCH RESET ============ */}
      <Dialog open={launchOpen} onOpenChange={(v) => { if (!v) { setLaunchOpen(false); setLaunchPw(""); } }}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="text-blue-700 flex items-center gap-2"><RefreshCw className="w-5 h-5"/> Persiapan Launch — Bersihkan History</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-md text-sm text-blue-900">
              <p className="font-semibold mb-2">Bersihkan history sebelum go-live agar mitra & entitas lain mulai dengan data bersih.</p>
              <p className="text-xs mb-1"><b>Yang akan DIHAPUS:</b></p>
              <ul className="text-xs list-disc pl-5 space-y-0.5">
                <li>Riwayat Opname (semua submission mitra)</li>
                <li>Stock Adjustment Request &amp; Audit Trail</li>
                <li>Keluhan mitra (test/dev)</li>
                <li>Pengumuman manual owner</li>
                <li>Log Aktivitas (login &amp; API call history)</li>
              </ul>
              <p className="text-xs mt-2"><b className="text-emerald-700">TETAP AMAN:</b> Produk &amp; stok, Distributor, Toko + PIC, User + password mitra, Modal, Transaksi keuangan (pembelian, penjualan, penyaluran, pengeluaran), Arus kas, Neraca.</p>
              <p className="text-xs mt-2 italic text-blue-700">Gunakan menu ini setelah sistem sudah di-test dan siap dipakai entitas resmi.</p>
            </div>
            <div><Label>Konfirmasi Password Owner</Label>
              <Input type="password" value={launchPw} onChange={(e) => setLaunchPw(e.target.value)} data-testid="input-launch-password"/>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setLaunchOpen(false); setLaunchPw(""); }}>Batal</Button>
            <Button onClick={doLaunchReset} disabled={!launchPw || launchBusy} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="btn-confirm-launch">
              {launchBusy ? "Membersihkan..." : "Bersihkan Sekarang"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
